import { Component, inject, input, InputSignal, OutputRef, ResourceRef, ChangeDetectionStrategy } from '@angular/core';
import { outputFromObservable, rxResource } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCard, MatCardContent } from '@angular/material/card';
import { DateComponent } from '@libs/form/components';
import { DateInputModel } from '@libs/form/models';
import { DeliveryWindowItem } from '@libs/shared/models';
import { DeliveryInfoService } from '@libs/shared/services';
import { TrackingGoogleService } from '@libs/shared/tracking';
import { DateTime } from 'luxon';
import { map } from 'rxjs/operators';

@Component({
  selector: 'ui-order-requested-delivery-date',
  imports: [DateComponent, ReactiveFormsModule, MatCard, MatCardContent],
  templateUrl: './order-requested-delivery-date.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './order-requested-delivery-date.component.scss',
})
export class OrderRequestedDeliveryDateComponent {

  public dateControl: FormControl<DateTime | null> = new FormControl<DateTime | null>(null);

  public refDate: InputSignal<DateTime> = input<DateTime>(DateTime.now());
  public deliveryDateSelected: OutputRef<DateTime | undefined> = outputFromObservable(
    this.dateControl.valueChanges.pipe(map(this.mapValue.bind(this))),
  );

  public deliveryWindowResource: ResourceRef<DeliveryWindowItem[] | undefined> = rxResource({
    params: () => this.refDate(),
    stream: ({ params: refDate }) => this.deliveryInfoService.getDeliveryWindows(refDate),
  });

  public data: DateInputModel = {
    type: 'date',
    name: '',
    label: 'Wunschlieferdatum',
    hint: '*Optional',
    filter: this.deliveryDayFilter.bind(this),
    validators: { dateFilter: true },
    subscriptSizing: 'dynamic',
    additionalHint: 'TT.MM.JJJJ',
  };

  private readonly deliveryInfoService: DeliveryInfoService = inject(DeliveryInfoService);
  private readonly tracking: TrackingGoogleService = inject(TrackingGoogleService);

  public deliveryDayFilter(date: DateTime | null): boolean {
    if (!date) {
      return false;
    }

    const deliveryDays: DeliveryWindowItem[] | undefined = this.deliveryWindowResource.value();

    return !!deliveryDays?.find(dd => dd.date.toISODate() === date.toISODate());
  }

  public onDatepickerToggleClicked(): void {
    this.tracking.trackOpenDeliveryWindowsCalendarEvent();
  }

  private mapValue(dateTime: DateTime | null): DateTime | undefined {
    if (!dateTime || !this.deliveryDayFilter(dateTime)) {
      return undefined;
    }

    const dayDiff: number = Number(dateTime.diffNow('day').days.toFixed(1));

    this.tracking.trackChangeDateDeliveryWindowsCalendarEvent(dayDiff);

    return dateTime;
  }
}
